<template>
  <div class="action-menu-container">
    <v-menu open-on-hover offset="4" content-class="action-menu-content" :close-on-content-click="false">
      <template #activator="{ props: activatorProps }">
        <v-btn
          :icon="menuIcon"
          size="small"
          variant="text"
          v-bind="activatorProps"
          tabindex="-1"
          style="width: 32px !important; height: 32px !important;"
        />
      </template>

      <v-list class="action-menu-list">
        <v-list-item
          v-for="item in menuItems"
          :key="item.id"
          class="menu-item"
          @click="!item.children && item.action?.()"
        >
          <div class="menu-item-content">
            <v-icon
              v-if="item.icon"
              size="20"
              :icon="item.icon"
              :color="item.color || 'grey-darken-1'"
            />
            <span>{{ item.text }}</span>
            <v-icon
              v-if="item.children"
              size="16"
              class="ml-auto text-grey-darken-1"
            >mdi-chevron-right</v-icon>
          </div>

          <v-menu
            v-if="item.children"
            activator="parent"
            open-on-hover
            location="end"
            offset="4"
            content-class="action-menu-content"
          >
            <v-list
              :selected="selectedMenu"
              class="action-menu-list"
              select-strategy="leaf"
              active-class="text-dark"
            >
              <v-list-item
                v-for="child in item.children"
                :key="child.id"
                :value="child.text.toLowerCase()"
                class="menu-item"
                @click="child.action?.()"
              >
                <div class="menu-item-content">
                  <v-icon
                    v-if="child.icon"
                    size="18"
                    :icon="child.icon"
                    color="grey-darken-1"
                  />
                  <span>{{ child.text }}</span>
                  <v-icon
                    v-if="child.text.toLowerCase() === 'object'"
                    size="14"
                    class="ml-auto text-grey-darken-1"
                  >mdi-chevron-right</v-icon>
                </div>

                <v-menu
                  v-if="child.text.toLowerCase() === 'object'"
                  activator="parent"
                  open-on-hover
                  location="end"
                  offset="4"
                  content-class="action-menu-content"
                >
                  <v-list
                    :selected="getSelectedEntityForType(child.text.toLowerCase())"
                    class="action-menu-list"
                    select-strategy="leaf"
                    active-class="text-dark"
                  >
                    <v-list-item
                      v-for="grand in selectableEntities || []"
                      :key="grand.id"
                      :value="grand.id"
                      class="menu-item"
                      @click="child.action?.(grand)"
                    >
                      <div class="menu-item-content">
                        <span>{{ grand.name }}</span>
                      </div>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script setup>
import { computed, toRefs } from 'vue'
import { useStore } from 'vuex'
import { SchemaType } from '@/constants/schema'

const props = defineProps({
  menuValue: { type: Object },
  menuIcon: { type: String, default: 'mdi-dots-vertical' },
  menuItems: { type: Array, default: () => [] }
})

const { menuValue, menuIcon, menuItems } = toRefs(props)

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const selectedMenu = computed(() => menuValue.value?.dataType ? [menuValue.value.dataType] : [])

const selectableEntities = computed(() => {
  return (workflow.value.schemas ?? []).filter((schema) => {
    return schema.type === SchemaType.ENTITY
  })
})

const getSelectedEntityForType = (dataTypeToCheck) => {
  if (menuValue.value?.dataType !== dataTypeToCheck) return []
  if (!menuValue.value?.relatedEntityId) return []

  const entity = selectableEntities.value.find(
    (schema) => schema.id === menuValue.value.relatedEntityId
  )

  return entity ? [entity.id] : []
}
</script>

<style scoped>
:deep(.v-btn) {
  width: 20px !important;
  height: 20px !important;
  color: #1f2833 !important;
  border-radius: 4px;
}

:deep(.v-btn:hover) {
  background-color: #e0e0e0 !important;
}

:deep(.v-btn__overlay) {
  background: transparent;
  opacity: 0;
}

:deep(.v-menu > .v-overlay__content) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-radius: 6px;
  overflow: hidden;
}

.action-menu-list {
  padding: 4px !important;
  border-radius: 6px;
  background-color: var(--light-1);
  min-width: 180px;
}

.menu-item {
  min-height: 32px !important;
  border-radius: 4px;
  cursor: pointer;
  padding: 0 8px !important;
}

.menu-item:hover {
  background-color: #e0e0e0 !important;
}

[data-theme="dark"] .menu-item:hover {
  background-color: var(--light-4) !important;
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Fix for v-list-item-title and v-list-item-subtitle to remove default padding */
:deep(.v-list-item__content) {
  padding: 0 !important;
}

:deep(.v-list-item-title) {
  font-size: 14px !important;
  font-weight: 500;
  line-height: 1.5;
  color: #1f2833;
}
</style>
